import React, { useEffect, useMemo, useState } from "react";
import {
  createSupportTicket,
  getMySupportTicket,
  listMySupportTickets,
  markMySupportTicketSeen,
  replyToMySupportTicket,
  type SupportTicketSeverity,
  type UserSupportTicketDetail,
  type UserSupportTicketEvent,
  type UserSupportTicketSummary,
} from "../api/supportTickets";
import {
  copyRedactedSupportBundle,
  createRedactedSupportBundle,
} from "../support/exportSupportBundle";
import {
  disableSupportDebugMode,
  enableSupportDebugMode,
  supportDebugModeEnabled,
} from "../support/debugFlags";

const SEVERITIES: SupportTicketSeverity[] = ["low", "normal", "high", "urgent"];

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function truncateText(value: any, max = 260) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function looksLikeDiagnosticJson(value: any) {
  const text = String(value || "").trim();
  return (
    text.startsWith("{") &&
    (text.includes('"version"') ||
      text.includes('"generatedAt"') ||
      text.includes('"createdAt"') ||
      text.includes('"warning"') ||
      text.includes('"logs"'))
  );
}

function cleanTicketPreview(ticket: any) {
  const candidates = [ticket?.latest_admin_reply, ticket?.issue_preview, ticket?.issue_text];

  for (const candidate of candidates) {
    const text = String(candidate || "").trim();
    if (!text) continue;
    if (looksLikeDiagnosticJson(text)) continue;
    return truncateText(text, 220);
  }

  return "";
}

function eventLabel(event: UserSupportTicketEvent) {
  const source = String(event?.meta?.source || "");
  if (source === "admin_reply") return "Support";
  if (source === "user_reply") return "You";
  if (event.event_type === "created") return "Ticket created";
  return event.event_type || "Update";
}

function eventText(event: UserSupportTicketEvent) {
  const note = String(event?.note || "").trim();
  if (!note) return "";
  if (looksLikeDiagnosticJson(note)) return "Diagnostic bundle attached.";
  return truncateText(note, 700);
}

function unread(v: any) {
  return v === true || v === 1;
}

export default function SupportPage() {
  const [subject, setSubject] = useState("");
  const [issue, setIssue] = useState("");
  const [severity, setSeverity] = useState<SupportTicketSeverity>("normal");

  const [supportDebugEnabled, setSupportDebugEnabled] = useState(() => supportDebugModeEnabled());
  const [busy, setBusy] = useState(false);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError] = useState("");

  const [tickets, setTickets] = useState<UserSupportTicketSummary[]>([]);
  const [ticketsBusy, setTicketsBusy] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<UserSupportTicketDetail | null>(null);
  const [events, setEvents] = useState<UserSupportTicketEvent[]>([]);
  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);

  const diagnostics = useMemo(() => {
    return [
      `ts=${new Date().toISOString()}`,
      `origin=${window.location.origin}`,
      `path=${window.location.pathname}`,
      `platform=web`,
    ].join("\n");
  }, []);

  async function loadTickets() {
    setTicketsBusy(true);
    setError("");

    try {
      const res = await listMySupportTickets();
      setTickets(res.items || []);
    } catch (e: any) {
      setError(e?.message || "Could not load support tickets.");
    } finally {
      setTicketsBusy(false);
    }
  }

  async function openTicket(publicId: string) {
    setTicketsBusy(true);
    setError("");

    try {
      const res = await getMySupportTicket(publicId);
      let ticket = res.ticket;

      if (unread(ticket.user_unread)) {
        try {
          const seen = await markMySupportTicketSeen(publicId);
          ticket = seen.ticket || ticket;
        } catch {
          // Non-fatal. Still show the ticket.
        }
      }

      setSelectedTicket(ticket);
      setEvents(res.events || []);
      setReply("");

      setTickets((prev) =>
        prev.map((item) =>
          item.public_id === publicId ? { ...item, ...ticket, user_unread: 0 } : item
        )
      );
    } catch (e: any) {
      setError(e?.message || "Could not open support ticket.");
    } finally {
      setTicketsBusy(false);
    }
  }

  async function handleCopyBundle() {
    setBundleBusy(true);
    setStatusMsg("");
    setError("");

    try {
      await copyRedactedSupportBundle({
        issueSubject: subject.trim() || undefined,
        issueSeverity: severity,
        submittedFrom: "web_support_page",
      });
      setStatusMsg("Redacted support bundle copied.");
    } catch (e: any) {
      setError(e?.message || "Could not copy support bundle.");
    } finally {
      setBundleBusy(false);
    }
  }

  async function handleSubmitTicket() {
    const cleanSubject = subject.trim() || "Support request";
    const cleanIssue = issue.trim();

    if (!cleanIssue) {
      setError("Please describe the issue before sending a support request.");
      return;
    }

    setBusy(true);
    setStatusMsg("");
    setError("");

    try {
      const bundle = createRedactedSupportBundle({
        issueSubject: cleanSubject,
        issueSeverity: severity,
        submittedFrom: "web_support_page",
      });

      const res = await createSupportTicket({
        subject: cleanSubject,
        issue: cleanIssue,
        severity,
        source: "web",
        platform: "web",
        bundle,
        logs: (bundle as any)?.logs || [],
        metadata: {
          submittedFrom: "web_support_page",
          supportDebugEnabled,
          createdAt: new Date().toISOString(),
          diagnostics,
        },
      });

      const ticketId = res.ticket?.public_id || res.ticket?.id || "";
      setStatusMsg(ticketId ? `Support request sent. Ticket: ${ticketId}` : "Support request sent.");
      setSubject("");
      setIssue("");
      setSeverity("normal");
      await loadTickets();
      if (ticketId) await openTicket(ticketId);
    } catch (e: any) {
      setError(e?.message || "Could not send support request.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReply() {
    if (!selectedTicket?.public_id) return;

    const cleanReply = reply.trim();
    if (!cleanReply) {
      setError("Please enter a reply first.");
      return;
    }

    setReplyBusy(true);
    setStatusMsg("");
    setError("");

    try {
      await replyToMySupportTicket(selectedTicket.public_id, cleanReply);
      setReply("");
      await openTicket(selectedTicket.public_id);
      await loadTickets();
      setStatusMsg("Reply sent to support.");
    } catch (e: any) {
      setError(e?.message || "Could not send reply.");
    } finally {
      setReplyBusy(false);
    }
  }

  function handleEnableDebug() {
    enableSupportDebugMode();
    setSupportDebugEnabled(true);
    setStatusMsg("Support debug mode enabled for 24 hours. Reproduce the issue, then send or copy a redacted support bundle.");
  }

  function handleDisableDebug() {
    disableSupportDebugMode();
    setSupportDebugEnabled(false);
    setStatusMsg("Support debug mode disabled.");
  }

  useEffect(() => {
    void loadTickets();
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Support</h1>
        <p className="text-sm text-slate-400">
          Send a support request with redacted diagnostics. Do not include passwords, tokens,
          private keys, plaintext messages, ciphertext, envelopes, or media.
        </p>
      </header>

      {!!statusMsg && (
        <div className="text-sm text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2">
          {statusMsg}
        </div>
      )}

      {!!error && (
        <div className="text-sm text-rose-300 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-4">
        <div>
          <div className="text-sm font-semibold text-slate-100">New support request</div>
          <div className="text-xs text-slate-400 mt-1">
            Describe what happened. A redacted diagnostic bundle will be attached automatically.
          </div>
        </div>

        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="w-full px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
        />

        <textarea
          value={issue}
          onChange={(e) => setIssue(e.target.value)}
          placeholder="What went wrong? What did you expect to happen?"
          rows={6}
          className="w-full px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
        />

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-400">
            Severity
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as SupportTicketSeverity)}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              {SEVERITIES.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </label>

          <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
            <div className="text-xs text-slate-400">Diagnostics</div>
            <pre className="mt-1 whitespace-pre-wrap text-[11px] text-slate-300">{diagnostics}</pre>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSubmitTicket}
            disabled={busy || !issue.trim()}
            className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Sending…" : "Send support request"}
          </button>

          <button
            type="button"
            onClick={handleCopyBundle}
            disabled={bundleBusy}
            className="px-4 py-2 rounded-xl border border-slate-700 text-slate-200 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            {bundleBusy ? "Copying…" : "Copy redacted bundle"}
          </button>

          <button
            type="button"
            onClick={supportDebugEnabled ? handleDisableDebug : handleEnableDebug}
            className="px-4 py-2 rounded-xl border border-slate-700 text-slate-200 text-sm font-semibold hover:bg-slate-800"
          >
            {supportDebugEnabled ? "Disable debug mode" : "Enable debug mode"}
          </button>
        </div>

        <div className="text-xs text-slate-500">
          Support debug mode is {supportDebugEnabled ? "enabled" : "disabled"}.
        </div>
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">My support tickets</div>
            <div className="text-xs text-slate-400 mt-1">
              View support replies and send more information when asked.
            </div>
          </div>

          <button
            type="button"
            onClick={loadTickets}
            disabled={ticketsBusy}
            className="px-3 py-1.5 rounded-xl border border-slate-700 text-slate-200 text-xs font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            {ticketsBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {tickets.length === 0 ? (
          <div className="text-sm text-slate-400">No support tickets yet.</div>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,1.05fr)] gap-4">
            <div className="space-y-2">
              {tickets.map((ticket) => {
                const active = selectedTicket?.public_id === ticket.public_id;
                const preview = cleanTicketPreview(ticket);

                return (
                  <button
                    key={ticket.public_id}
                    type="button"
                    onClick={() => openTicket(ticket.public_id)}
                    className={[
                      "w-full text-left rounded-2xl border p-3 transition-colors",
                      active
                        ? "border-pink-500 bg-pink-950/20"
                        : "border-slate-800 bg-slate-950/40 hover:bg-slate-800/50",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-2">
                      {unread(ticket.user_unread) && (
                        <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black text-white">
                          NEW
                        </span>
                      )}
                      <div className="min-w-0 flex-1 text-sm font-semibold text-slate-100 truncate">
                        {ticket.subject || "Support request"}
                      </div>
                    </div>

                    <div className="mt-1 text-xs text-slate-400">
                      {ticket.status} · {ticket.severity} · updated {fmtDate(ticket.updated_at || ticket.created_at)}
                    </div>

                    {!!preview && (
                      <div className="mt-2 text-xs text-slate-300">{preview}</div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-3">
              {!selectedTicket ? (
                <div className="text-sm text-slate-400">Select a ticket to view details.</div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">
                      {selectedTicket.subject || "Support request"}
                    </div>
                    <div className="mt-1 text-xs text-slate-400 break-all">
                      {selectedTicket.public_id} · {selectedTicket.status} · {selectedTicket.severity}
                    </div>
                    {!!selectedTicket.external_issue_key && (
                      <div className="mt-1 text-xs text-slate-400">
                        Linked tracker: {selectedTicket.external_issue_provider || "tracker"} · {selectedTicket.external_issue_key}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {events.map((event) => {
                      const text = eventText(event);
                      return (
                        <div
                          key={event.id}
                          className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"
                        >
                          <div className="text-xs font-semibold text-slate-200">{eventLabel(event)}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(event.created_at)}</div>
                          {!!text && <div className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{text}</div>}
                        </div>
                      );
                    })}
                  </div>

                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Add more information for support…"
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />

                  <button
                    type="button"
                    onClick={handleReply}
                    disabled={replyBusy || !reply.trim()}
                    className="px-4 py-2 rounded-xl bg-pink-500 text-white text-sm font-semibold hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {replyBusy ? "Sending…" : "Send reply"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
